import { constantRoutes } from '@/router'
import {
  PC_PERMISSION_TREE,
  MOBILE_PERMISSION_TREE,
  OPERATION_PERMISSION_TREE,
  ALL_LEAF_IDS
} from '@/config/permissionNodes'

/** 构建 PC 后台权限树 */
export function buildPcPermissionTree() {
  return PC_PERMISSION_TREE
}

/** 构建移动端后台权限树 */
export function buildMobilePermissionTree() {
  return MOBILE_PERMISSION_TREE
}

/** 构建操作权限树 */
export function buildOperationPermissionTree() {
  return OPERATION_PERMISSION_TREE
}

/** 兼容旧调用：返回 PC 权限树 */
export function buildPermissionTree() {
  return buildPcPermissionTree()
}

/** 解析 permissions 字段为数组 */
export function parsePermissions(value) {
  if (!value) return []
  if (Array.isArray(value)) return value
  if (typeof value === 'string') {
    const text = value.trim()
    if (!text) return []
    try {
      const parsed = JSON.parse(text)
      return Array.isArray(parsed) ? parsed : []
    } catch (e) {
      return []
    }
  }
  return []
}

/** 是否拥有全部权限 */
export function hasAllPermissions(permissions) {
  return parsePermissions(permissions).includes('*')
}

/** 判断单个权限节点 */
export function hasPermission(permissions, permId) {
  const list = parsePermissions(permissions)
  if (list.includes('*')) return true
  if (!permId) return false
  return list.includes(permId)
}

/** 判断单个菜单权限（路由 name） */
export function hasMenuPermission(permissions, routeName) {
  return hasPermission(permissions, routeName)
}

/** 判断操作权限 */
export function hasOperationPermission(permissions, opKey) {
  return hasPermission(permissions, opKey)
}

/** 过滤驿站管理员不可见的 superOnly 子菜单 */
export function filterSuperOnlyRoutes(routes, isStationAdmin) {
  if (!isStationAdmin) return routes
  return routes
    .filter(route => route.path !== '/admin')
    .map(route => {
      if (!route.children || !route.children.length) return route
      const children = route.children.filter(child => !(child.meta && child.meta.superOnly))
      if (!children.length) return null
      return { ...route, children }
    })
    .filter(Boolean)
}

/** 判断路由分组是否应显示（任一子菜单有权限） */
export function filterRoutesByPermission(routes, permissions) {
  if (hasAllPermissions(permissions)) return routes

  return routes
    .map(route => {
      if (!route.children || !route.children.length) {
        return hasMenuPermission(permissions, route.name) ? route : null
      }
      const children = route.children.filter(child => {
        if (child.hidden) return false
        return hasMenuPermission(permissions, child.name)
      })
      if (!children.length) return null
      return { ...route, children }
    })
    .filter(Boolean)
}

/** 检查路由是否可访问 */
export function canAccessRoute(permissions, route) {
  if (hasAllPermissions(permissions)) return true
  const list = parsePermissions(permissions)
  if (!list.length) return true
  if (route.meta && route.meta.superOnly) {
    const matched = route.matched || []
    const parentSuper = matched.some(r => r.meta && r.meta.superOnly)
    if (parentSuper && !list.some(p => ['AdminList', 'AdminRole'].includes(p))) {
      return false
    }
  }
  const name = route.name
  if (!name) return true
  return hasMenuPermission(permissions, name)
}

/** 是否可加载全量驿站列表（驿站管理权限或超管） */
export function canLoadSubsiteList(permissions) {
  if (hasAllPermissions(permissions)) return true
  const parsed = parsePermissions(permissions)
  if (!parsed.length) return true
  return hasMenuPermission(permissions, 'SubSite')
}

/** 收集所有可勾选叶子节点 */
export function allPermissionLeafKeys() {
  return ALL_LEAF_IDS.slice()
}

export { constantRoutes }
